import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthDialog } from "@/components/AuthDialog";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BeachSelector from "@/components/BeachSelector";
import WeatherCard from "@/components/WeatherCard";
import SurfReport from "@/components/SurfReport";
import TideChart from "@/components/TideChart";
import AlertsPanel, { useAlertCount } from "@/components/AlertsPanel";
import ChatBox from "@/components/ChatBox";
import WeatherRadar from "@/components/WeatherRadar";
import LiveSurfReport from "@/components/LiveSurfReport";
import HistoryTeaser from "@/components/HistoryTeaser";
import AdminPanel from "@/components/AdminPanel";
import ShareButton from "@/components/ShareButton";
import Typewriter from "@/components/Typewriter";
import { useSEO } from "@/hooks/useSEO";
import { Loader2 } from "lucide-react";

// Below-the-fold sections imported eagerly (on-demand chunks hang behind the
// preview proxy; a single bundle loads reliably).
import SurfSessions from "@/components/SurfSessions";
import SurfPlans from "@/components/SurfPlans";
import EventsSection from "@/components/EventsSection";
import NonProfits from "@/components/NonProfits";
import NicaraguaTaxiCTA from "@/components/NicaraguaTaxiCTA";
import SuggestionsSection from "@/components/SuggestionsSection";
import EmergencyContacts from "@/components/EmergencyContacts";
import LocalPartners from "@/components/LocalPartners";
import SjdsCrossPromo from "@/components/SjdsCrossPromo";
import DigitalNomadPromo from "@/components/DigitalNomadPromo";

function SectionSkeleton({ minHeight = 180 }) {
  return (
    <div
      className="clay p-6 animate-pulse bg-[color:var(--sand-2)]/60"
      style={{ minHeight }}
      aria-hidden
    />
  );
}

const HERO_IMG = "https://images.pexels.com/photos/5232415/pexels-photo-5232415.jpeg?auto=compress&cs=tinysrgb&w=1920";

export default function Home() {
  const { t, lang, setLang } = useI18n();
  const { user } = useAuth();
  const [beachId, setBeachId] = useState("playa-popoyo");
  const { open: openAuth } = useAuthDialog();
  const queryClient = useQueryClient();
  const prefsAppliedRef = useRef(false);

  // Apply saved preferences once after login
  useEffect(() => {
    if (!user || !user.id || prefsAppliedRef.current) return;
    prefsAppliedRef.current = true;
    if (user.preferred_beach_id) queueMicrotask(() => setBeachId(user.preferred_beach_id));
    if (user.preferred_lang && user.preferred_lang !== lang) queueMicrotask(() => setLang(user.preferred_lang));
  }, [user, lang, setLang]);

  // Persist beach changes for logged-in users (skip the initial apply)
  useEffect(() => {
    if (!user || !user.id) return;
    if (!prefsAppliedRef.current) return;
    if (user.preferred_beach_id === beachId) return;
    const handle = setTimeout(() => {
      http.patch("/auth/me", { preferred_beach_id: beachId }).catch(() => {});
    }, 600);
    return () => clearTimeout(handle);
  }, [beachId, user]);

  // Persist lang changes for logged-in users
  useEffect(() => {
    if (!user || !user.id) return;
    if (!prefsAppliedRef.current) return;
    if (user.preferred_lang === lang) return;
    const handle = setTimeout(() => {
      http.patch("/auth/me", { preferred_lang: lang }).catch(() => {});
    }, 600);
    return () => clearTimeout(handle);
  }, [lang, user]);

  const beachesQ = useQuery({
    queryKey: ["beaches"],
    queryFn: async () => (await http.get("/beaches")).data,
  });

  const surfQ = useQuery({
    queryKey: ["surf", beachId],
    queryFn: async () => (await http.get(`/surf/${beachId}`, { params: { days: 7 } })).data,
    enabled: !!beachId,
    refetchInterval: 15 * 60_000,
    placeholderData: keepPreviousData,
  });

  const alertCount = useAlertCount();

  // Per-route SEO + share metadata
  useSEO({
    title: "Popoyo, Nicaragua · Live Surf, Tides, Weather & Community — Popoyo.app",
    description:
      "Everything you need for Popoyo, Nicaragua: live surf reports, tide charts, weather radar, earthquake & tsunami alerts, moon calendar, community chat, event calendar, local business directory and the complete history of Popoyo.",
    path: "/",
  });

  return (
    <div className="min-h-screen flex flex-col bg-[color:var(--sand)]">
      <Header alertCount={alertCount} />

      {/* HERO */}
      <section
        data-testid="hero"
        className="relative w-full overflow-hidden"
        style={{
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center 40%",
          minHeight: 320,
        }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(27,59,111,0.35) 0%, rgba(27,59,111,0.55) 60%, rgba(253,251,247,0.95) 100%)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 flex items-start gap-6">
          <img
            src="/brand/logo.png"
            alt="Popoyo.app logo"
            data-testid="hero-logo"
            className="hidden md:block w-36 h-36 lg:w-44 lg:h-44 rounded-full shadow-2xl object-cover shrink-0"
            style={{ background: "#FDFBF7", boxShadow: "0 20px 50px -20px rgba(0,0,0,0.45), 0 0 0 4px rgba(255,255,255,0.3)" }}
          />
          <div>
            <p className="eyebrow !text-white mb-3" style={{ color: "#ffffff", textShadow: "0 1px 12px rgba(0,0,0,0.35)" }}>Popoyo.app · Tola, Nicaragua</p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white max-w-3xl"
              style={{ fontFamily: "Outfit", textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
              data-testid="hero-headline"
            >
              <Typewriter text={t("app_tagline")} speed={32} startDelay={220} testId="hero-typewriter" />
            </h1>
            <p className="mt-3 text-base sm:text-lg text-white/95 max-w-2xl" style={{ textShadow: "0 1px 14px rgba(0,0,0,0.4)" }}>
              {t("app_subtagline")}
            </p>
            <div className="mt-5">
              <ShareButton
                path="/"
                title="Popoyo.app — Popoyo Surf, Tides & Community"
                text="Live radar, surf reports, push alerts and a community chat for Popoyo, Nicaragua."
                testId="share-home-btn"
              />
            </div>
          </div>
        </div>
      </section>

      <div className="wave-divider" />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        {/* ADMIN DASHBOARD — only renders for users with role=admin (returns null otherwise) */}
        <div id="admin" className="mb-6 scroll-mt-24">
          <AdminPanel />
        </div>

        {/* Beach selector */}
        <div className="clay p-5 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="beach-selector-wrap">
          <BeachSelector
            beaches={beachesQ.data || []}
            beachId={beachId}
            onChange={setBeachId}
          />
          <p className="text-xs text-[color:var(--ink-2)] hidden md:block">
            {t("saved_to_phone")}
          </p>
        </div>

        {/* SISTER SITE — cross-promo to SJDS.app, mirrors the Popoyo card on sjds.app */}
        <div className="mb-6 space-y-3">
          <SjdsCrossPromo lang={lang} />
          <DigitalNomadPromo lang={lang} />
        </div>

        {/* LIVE SURF REPORT — admin-managed video feed, above the Windy radar */}
        <div id="live-surf" className="mb-6 scroll-mt-24">
          <LiveSurfReport />
          <div className="mt-2 text-right">
            <a href="/live-surf-cam" data-testid="home-livecam-link" className="text-sm font-semibold text-[color:var(--coral)] hover:underline inline-flex items-center gap-1">
              {t("nav_live")} →
            </a>
          </div>
        </div>

        {/* LIVE WEATHER RADAR — top of page so people land on the storm view */}
        <div id="weather" className="mb-6 scroll-mt-24">
          <WeatherRadar beach={(beachesQ.data || []).find((b) => b.id === beachId)} />
        </div>

        {/* HISTORY TEASER — sits directly under the live radar */}
        <div className="mb-6">
          <HistoryTeaser />
        </div>

        {/* SURF + WEATHER DATA GRID — surf report, current conditions, tides, alerts */}
        {surfQ.isLoading && (
          <div className="flex items-center justify-center py-12 text-[color:var(--ink-2)]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t("loading_surf")}
          </div>
        )}
        {surfQ.data && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 md:gap-6 mb-6">
            <div className="lg:col-span-3">
              <SurfReport
                data={surfQ.data}
                onNoteSaved={() => queryClient.invalidateQueries({ queryKey: ["surf", beachId] })}
              />
            </div>
            <div className="lg:col-span-3">
              <WeatherCard data={surfQ.data} />
            </div>
            <div className="lg:col-span-6">
              <TideChart tide={surfQ.data.tide} moon={surfQ.data.moon} />
            </div>
            <div className="lg:col-span-6">
              <AlertsPanel />
            </div>
          </div>
        )}

        {/* CHAT — community general chat */}
        <div id="community" className="mb-6 scroll-mt-24">
          <ChatBox onOpenAuth={() => openAuth("login")} />
        </div>

        {/* SURF SESSIONS — community meet-up calendar */}
        <div id="surf" className="mb-6 scroll-mt-24">
          <Suspense fallback={<SectionSkeleton minHeight={320} />}>
            <SurfSessions
              beaches={beachesQ.data || []}
              onOpenAuth={() => openAuth("login")}
            />
          </Suspense>
        </div>

        {/* COMMUNITY EVENTS — surf contests, yoga, moon parties, live music */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={280} />}>
            <EventsSection />
          </Suspense>
        </div>

        {/* PERSONAL SURF PLANS + FRIEND INVITES */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={260} />}>
            <SurfPlans
              beaches={beachesQ.data || []}
              onOpenAuth={() => openAuth("login")}
            />
          </Suspense>
        </div>

        {/* HELP THE COMMUNITY — local non-profits & donation links */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={220} />}>
            <NonProfits />
          </Suspense>
        </div>

        {/* SUGGESTIONS — community → admin feature requests */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={200} />}>
            <SuggestionsSection />
          </Suspense>
        </div>

        {/* NICARAGUA TAXI — sponsored transportation CTA, placed right above emergency */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={140} />}>
            <NicaraguaTaxiCTA />
          </Suspense>
        </div>

        {/* EMERGENCY CONTACTS — final block before partners + footer */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={260} />}>
            <EmergencyContacts />
          </Suspense>
        </div>

        {/* LOCAL PARTNERS — paid websites Greg has built for Popoyo businesses */}
        <div className="mb-6">
          <Suspense fallback={<SectionSkeleton minHeight={320} />}>
            <LocalPartners />
          </Suspense>
        </div>
      </main>

      <Footer />
    </div>
  );
}
